import { useEffect, useRef, useState } from 'react';

// Page-wide file drag-and-drop. Lets the user drop files/folders ANYWHERE on the
// page (not just a small target) — the window catches the drop, recurses folders,
// and hands back the flat file list. `dragging` drives a drop-hint overlay. Only
// reacts to file drags, so it doesn't interfere with in-page DnD (column reorder).

export interface DroppedFile {
  file: File;
  path: string;
}

function readAllEntries(reader: any): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const all: any[] = [];
    const batch = () =>
      reader.readEntries((es: any[]) => {
        if (!es.length) return resolve(all);
        all.push(...es);
        batch();
      }, reject);
    batch();
  });
}

async function walkEntry(entry: any, path: string, out: DroppedFile[]): Promise<void> {
  if (entry.isFile) {
    const file: File = await new Promise((res, rej) => entry.file(res, rej));
    out.push({ file, path: path ? `${path}/${entry.name}` : entry.name });
  } else if (entry.isDirectory) {
    const entries = await readAllEntries(entry.createReader());
    const dir = path ? `${path}/${entry.name}` : entry.name;
    for (const e of entries) await walkEntry(e, dir, out);
  }
}

// collectFiles grabs the FileSystemEntry objects synchronously (they're only
// valid during the drop event) and then recurses them.
export async function collectFiles(dt: DataTransfer): Promise<DroppedFile[]> {
  const out: DroppedFile[] = [];
  const entries: any[] = [];
  if (dt.items && dt.items.length && (dt.items[0] as any).webkitGetAsEntry) {
    for (let i = 0; i < dt.items.length; i++) {
      const en = (dt.items[i] as any).webkitGetAsEntry();
      if (en) entries.push(en);
    }
    for (const en of entries) await walkEntry(en, '', out);
  } else if (dt.files) {
    for (let i = 0; i < dt.files.length; i++) out.push({ file: dt.files[i], path: dt.files[i].name });
  }
  return out;
}

const hasFiles = (dt: DataTransfer | null) => !!dt && Array.from(dt.types || []).includes('Files');

// `enabled` gates the whole thing: since the listeners live on window, a hidden
// tab that stays mounted would otherwise keep catching drops. Callers that share
// the window with sibling tabs pass `pathname === theirPath` so only the visible
// tab is live.
export function usePageFileDrop(onFiles: (files: DroppedFile[]) => void, enabled = true): boolean {
  const [dragging, setDragging] = useState(false);
  const depthRef = useRef(0); // enter/leave can nest across child elements
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  useEffect(() => {
    if (!enabled) {
      depthRef.current = 0;
      setDragging(false);
      return;
    }
    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      depthRef.current++;
      setDragging(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      e.preventDefault(); // required to allow the drop
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setDragging(false);
    };
    const onDrop = async (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      e.preventDefault();
      depthRef.current = 0;
      setDragging(false);
      const files = await collectFiles(e.dataTransfer!);
      if (files.length) onFilesRef.current(files);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [enabled]);

  return enabled && dragging;
}
